import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import { fetcher, CACHE_DURATION_MS } from './utils';
// import { StockService } from '../../services/stockService';
// import { useCustomer } from '../../contexts/CustomerContext';
import './styles.scss';
import { StockServiceContext } from '../../context/stock';
import { TickerDataPoint, TickersResponse, TickerInfo } from '../../providers/stock/types';
import { useRecoilValue } from 'recoil';
import { CustomerAtom } from '../../atoms/customerAtom';
import { LoginPage } from '../loginPage';
import { useTickerSearch } from '../../hooks/useTickerSearch';
import { ShowCandlestickChart } from './Chart';

interface CandlestickChartProps {
  initialIsin?: string;
}

export const CandlestickChart: React.FC<CandlestickChartProps> = ({ initialIsin }) => {
  const tisin = initialIsin || '';
  const navigate = useNavigate();
  const stockService = useContext(StockServiceContext);
  const customer = useRecoilValue(CustomerAtom);

  // Chart refs are now managed by ShowCandlestickChart component
  const [isin, setIsin] = useState<string>('');
  const [seachedIsin, setSearchedIsin] = useState<string>('');
  const [shouldSearch, setShouldSearch] = useState<boolean>(false);
  const [autoShouldSearch, setAutoShouldSearch] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedTicker, setSelectedTicker] = useState<TickerInfo | null>(null);

  // Calculate dates
  const lookbackDays = 200;
  const endDate = new Date().toISOString().split('T')[0];
  const startDate = new Date(Date.now() - lookbackDays * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  useEffect(() => {
    if (tisin && isin === '') {
      setIsin(tisin);
      setSearchedIsin(tisin);
      setAutoShouldSearch(true);
      setShouldSearch(true);
    } else if (isin !== '' && tisin !== isin && shouldSearch && isin === seachedIsin) {
      setSearchedIsin("");
      navigate(`/candlestick/${isin}`);
    } else if (isin !== seachedIsin && isin !== initialIsin && shouldSearch) {
      setIsin(initialIsin as string);
    }
  }, [isin, tisin, shouldSearch, initialIsin, navigate]);

  let accessToken = customer?.access_token;
  const exchange = 'NSE';
  const type = 'EQ';

  // SWR configuration with persistence for ticker list
  const { data: allTickersList, error: errorr, isLoading } = useSWR(
    customer?.access_token ? `ticker_${type}_${exchange}` : null,
    async (url) => {
      const cached = await fetcher(url);
      if (cached) {
        return cached;
      }
      if (!stockService || !customer?.api_key || !customer?.api_secret) {
        throw new Error('Missing required credentials');
      }

      const response = await stockService.getTickers(accessToken as string, 'EQ', 'NSE');

      if (response) {
        try {
          localStorage.setItem(url, JSON.stringify({
            data: response,
            timestamp: Date.now()
          }));
        } catch (error) {
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key?.startsWith('ticker_')) {
              localStorage.removeItem(key);
            }
          }
          localStorage.setItem(url, JSON.stringify({
            data: response,
            timestamp: Date.now()
          }));
        }
      }

      return response;
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 24 * 60 * 60 * 1000, // Cache for 1 day
    }
  );

  const { searchResults } = useTickerSearch({
    tickers: allTickersList?.data || [],
    searchTerm: searchTerm
  });

  // SWR configuration with persistence for stock data of selected ISIN
  const { data: stockDataResponse, error } = useSWR(
    shouldSearch && isin && stockService && customer?.api_key && customer?.api_secret
      ? `stockdata/1d/${isin}/${startDate}/${endDate}`
      : null,
    async (url) => {
      setSearchedIsin("");
      const cached = await fetcher(url);
      if (cached) {
        return cached;
      }

      if (!stockService || !customer?.api_key || !customer?.api_secret) {
        throw new Error('Missing required credentials');
      }

      let accessToken = customer.access_token;
      if (!accessToken) {
        const accessTokenResponse = await stockService.GetAccessToken(customer.api_key, customer.api_secret);
        accessToken = accessTokenResponse.accessToken;
      }

      let response = await stockService.getTickerData(
        isin,
        '1d',
        startDate,
        endDate,
        accessToken
      );

      if (response?.status === '401') {
        const accessTokenResponse = await stockService.GetAccessToken(customer.api_key, customer.api_secret);
        response = await stockService.getTickerData(
          isin,
          '1d',
          startDate,
          endDate,
          accessTokenResponse.accessToken
        );
      }

      if (response) {
        try {
          localStorage.setItem(url, JSON.stringify({
            data: response,
            timestamp: Date.now()
          }));
        } catch (error) {
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key?.startsWith('stockdata/')) {
              localStorage.removeItem(key);
            }
          }
          localStorage.setItem(url, JSON.stringify({
            data: response,
            timestamp: Date.now()
          }));
        }
      }

      return response;
    },
    {
      dedupingInterval: CACHE_DURATION_MS,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  // Chart initialization and data management is now handled by ShowCandlestickChart component

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    setIsin(value);
    setShouldSearch(false);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      const value = event.currentTarget.value;
      setIsin(value);
      setSearchedIsin(value);
      setShouldSearch(true);
    }
  };

  const handleTickerSelect = (ticker: TickerInfo) => {
    setIsin(ticker.isin);
    setSearchedIsin(ticker.isin);
    setShouldSearch(true);
    setSearchTerm('');
    setSelectedTicker(ticker);
  };

  if (!customer?.api_key) {
    return <LoginPage />;
  }

  return (
    <div className="candlestick-chart-wrapper">
      <div className="candlestick-chart-header">
        <div className="candlestick-chart-search">
          <label htmlFor="isin-search" className="candlestick-chart-label">
            ISIN Search
          </label>
          <div className="relative">
            <input
              id="isin-search"
              type="text"
              value={searchTerm}
              onChange={handleSearch}
              onKeyPress={handleKeyPress}
              placeholder="Enter ISIN code (e.g., INE758T01015)"
              className="candlestick-chart-input"
            />
            {searchResults.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white rounded-md shadow-lg">
                <ul className="py-1">
                  {searchResults.map((ticker) => (
                    <li
                      key={ticker.isin}
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                      onClick={() => handleTickerSelect(ticker)}
                    >
                      <div className="font-medium">{ticker.ticker}</div>
                      <div className="text-sm text-gray-500">{ticker.name}</div>
                      <div className="text-xs text-gray-400">{ticker.isin}</div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
        <div className="candlestick-chart-status">
          {selectedTicker && (
            <span>
              Chart for <b>{selectedTicker.short_name}</b>
            </span>
          )}
          {!selectedTicker && searchResults.length == 0 && (
            <span>
              {isin ? `Getting data for: ${isin} ....` : 'Enter an ISIN to search'}
            </span>
          )}
        </div>
      </div>
      <ShowCandlestickChart stockData={stockDataResponse} />
    </div>
  );
};
